/**
 * Generates pie chart questions for Pie Chart Puzzles game.
 */

const shuffle = (arr) => {
    const a = [...arr];
    for (let i = a.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [a[i], a[j]] = [a[j], a[i]];
    }
    return a;
};

/**
 * Create simple pie chart data - 3-4 segments that add to 100.
 */
const generatePieData = (difficulty = "normal") => {
    let numSegments = 3 + Math.floor(Math.random() * 2); // 3 or 4
    if (difficulty === "easy") {
        numSegments = 3;
    } else if (difficulty === "hard") {
        numSegments = 4;
    }
    const names = ['Red', 'Blue', 'Green', 'Yellow', 'Orange', 'Purple'].slice(0, numSegments);
    let remaining = 100;
    const values = [];
    for (let i = 0; i < numSegments - 1; i++) {
        const v = Math.floor(Math.random() * (remaining - (numSegments - i - 1) * 10)) + 10;
        values.push(Math.min(v, remaining - 10));
        remaining -= values[values.length - 1];
    }
    values.push(remaining);
    return names.map((name, i) => ({ name, value: values[i], color: getColor(name) }));
};

const getColor = (name) => {
    const map = {
        Red: '#e53935',
        Blue: '#1976d2',
        Green: '#43a047',
        Yellow: '#fbc02d',
        Orange: '#fb8c00',
        Purple: '#7b1fa2',
    };
    return map[name] || '#9e9e9e';
};

const gcd = (a, b) => {
    let x = Math.abs(a);
    let y = Math.abs(b);
    while (y) {
        [x, y] = [y, x % y];
    }
    return x || 1;
};

/**
 * Helpers for comparing and mutating pie data.
 */
const isSamePieData = (a, b) => {
    if (!a || !b || a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) {
        if (a[i].name !== b[i].name || a[i].value !== b[i].value) return false;
    }
    return true;
};

const cloneWithValues = (base, values) =>
    base.map((seg, idx) => ({
        ...seg,
        value: values[idx],
    }));

// Create a wrong variant by shuffling the values across the same labels.
const createShuffledVariant = (base) => {
    const values = base.map((s) => s.value);
    let shuffled = values;
    // Re-shuffle until the order actually changes
    for (let attempts = 0; attempts < 10; attempts++) {
        shuffled = shuffle(values);
        let different = false;
        for (let i = 0; i < values.length; i++) {
            if (shuffled[i] !== values[i]) {
                different = true;
                break;
            }
        }
        if (different) break;
    }
    return cloneWithValues(base, shuffled);
};

/**
 * Type A: What percentage does [segment] represent?
 * Used primarily for easy difficulty (reading rounded percentages).
 */
export const generatePiePercentQuestion = (difficulty = "normal") => {
    const data = generatePieData(difficulty);
    const idx = Math.floor(Math.random() * data.length);
    const segment = data[idx];
    const answer = String(segment.value);
    const distractors = data
        .filter((_, i) => i !== idx)
        .map((d) => String(d.value));
    while (distractors.length < 3) {
        const wrong = Math.floor(Math.random() * 100) + 1;
        if (!data.some((d) => d.value === wrong) && !distractors.includes(String(wrong))) {
            distractors.push(String(wrong));
        }
    }
    const options = shuffle([answer, ...distractors.slice(0, 3)]);

    return {
        mode: 'percent-of-segment',
        question: `What percentage does the ${segment.name} section represent?`,
        data,
        answer,
        options,
        type: 'pie',
    };
};

/**
 * Type B: Which section is the largest? (multiple choice: Red, Blue, etc.)
 * Used primarily for easy difficulty (compare percentages).
 */
export const generatePieLargestQuestion = (difficulty = "normal") => {
    const data = generatePieData(difficulty);
    const largest = data.reduce((a, b) => (a.value > b.value ? a : b));
    const answer = largest.name;
    const allNames = ['Red', 'Blue', 'Green', 'Yellow', 'Orange', 'Purple'];
    const wrongNames = allNames.filter((n) => !data.some((d) => d.name === n) || n !== answer);
    const distractors = data
        .filter((d) => d.name !== answer)
        .map((d) => d.name);
    while (distractors.length < 3) {
        const n = wrongNames[Math.floor(Math.random() * wrongNames.length)];
        if (!distractors.includes(n)) distractors.push(n);
    }
    const options = shuffle([answer, ...distractors.slice(0, 3)]);

    return {
        mode: 'largest',
        question: `Which section is the largest?`,
        data,
        answer,
        options,
        type: 'pie',
    };
};

/**
 * Type C: Table-to-pie – show a table, pick the matching pie chart.
 */
export const generatePieTableMatchQuestion = (difficulty = "normal") => {
    const tableData = generatePieData(difficulty);

    // Start with the correct data as one of the options
    const pieOptions = [tableData];

    // Generate 3 distinct wrong variants
    while (pieOptions.length < 4) {
        const variant = createShuffledVariant(tableData);
        if (!pieOptions.some((opt) => isSamePieData(opt, variant))) {
            pieOptions.push(variant);
        }
    }

    const shuffledOptions = shuffle(pieOptions);
    const correctIndex = shuffledOptions.findIndex((opt) => isSamePieData(opt, tableData));

    return {
        mode: 'table-to-pie',
        question: 'Which pie chart matches this table?',
        tableData,
        pieOptions: shuffledOptions,
        answer: String(correctIndex),
        type: 'pie',
    };
};

/**
 * Medium/Hard: Calculate counts from percentages given total.
 */
export const generatePieCountQuestion = (difficulty = "normal") => {
    const data = generatePieData(difficulty);
    const idx = Math.floor(Math.random() * data.length);
    const segment = data[idx];

    // Choose total so that count is a whole number of students
    const pct = segment.value || 1;
    const step = 100 / gcd(100, pct); // smallest total where pct% gives an integer
    const k = Math.floor(Math.random() * 8) + 2; // 2-9
    const total = step * k;
    const count = (pct * total) / 100;

    const correct = Math.round(count);
    const distractors = [];
    const offsets = [2, 5, 10, -2, -5, -10];
    const seen = new Set([correct]);
    while (distractors.length < 3) {
        const off = offsets[Math.floor(Math.random() * offsets.length)];
        const cand = correct + off;
        if (cand > 0 && !seen.has(cand)) {
            seen.add(cand);
            distractors.push(cand);
        }
    }

    const options = shuffle([correct, ...distractors]).map((v) => String(v));

    return {
        mode: 'count-from-percent',
        question: `This pie chart shows how ${total} students voted. How many students chose ${segment.name}?`,
        data,
        total,
        answer: String(correct),
        options,
        type: 'pie',
    };
};

/**
 * Pick a random pie chart question type.
 */
export const generatePieChartQuestion = (difficulty = "normal") => {
    if (difficulty === "easy") {
        // Easy: read and compare simple rounded percentages only
        const fns = [generatePiePercentQuestion, generatePieLargestQuestion];
        const fn = fns[Math.floor(Math.random() * fns.length)];
        return fn("easy");
    }

    if (difficulty === "hard") {
        // Hard: reverse logic / matching and multi-step style via table-to-pie & count questions
        const fns = [generatePieTableMatchQuestion, generatePieCountQuestion];
        const fn = fns[Math.floor(Math.random() * fns.length)];
        return fn("hard");
    }

    // Medium: calculate numbers from percentages
    return generatePieCountQuestion("normal");
};
